import { computed, Signal } from '@preact/signals-react';
import { CompoundChange } from '@/pkg/sdk';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';

export function useCssManipulator(
  change: Signal<EditorDeclarativeBlock>,
  setCss: (css: string) => void,
) {
  const css = computed(() => {
    const block = change.value.block.value as CompoundChange;
    return block.css || ``;
  });

  const parsedCss = computed(() => {
    try {
      const styles = new CSSStyleSheet();
      // @ts-ignore
      styles.replaceSync(css.value);
      return styles.cssRules;
    } catch (error) {
      console.error(`vsly-editor`, `CSS parsing error:`, error);
      return undefined;
    }
  });

  const getCssRule = (selector: string): ParsedCssRule => {
    if (!parsedCss.value) return undefined;
    // eslint-disable-next-line no-restricted-syntax
    for (const rule of parsedCss.value) {
      if (rule instanceof CSSStyleRule && rule.selectorText === selector) {
        const allDecls = rule.style.cssText.split(`;`);
        const decls = allDecls
          .map((decl) => decl.split(`:`))
          .flatMap((decls) => ({
            name: decls[0]?.trim(),
            value: decls[1]?.trim(),
          }))
          .filter((decl) => decl.name && decl.value);
        return {
          selector,
          declarations: decls,
        };
      }
    }
    return { selector, declarations: [] };
  };

  const setCssRule = (input: ParsedCssRule) => {
    if (!parsedCss.peek()) return;
    const newCss = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const rule of parsedCss.peek()) {
      if (
        rule instanceof CSSStyleRule &&
        rule.selectorText === input.selector
      ) {
        rule.style.cssText = input.declarations
          .map((v) => `${v.name}: ${v.value}`)
          .join(`;\n`);
      }
      newCss.push(rule.cssText);
    }
    const updated = newCss.join(`\n\n`);
    setCss(updated);
  };

  return {
    parsedCss,
    getCssRule,
    setCssRule,
  };
}

export interface ParsedCssRule {
  selector: string;
  declarations: { name: string; value: string }[];
}
